import type { ColumnDef } from '@tanstack/react-table';
import { useState, useMemo, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import DataGridTable from '@components/DataGridTable/DataGridTable';
import Icon from '@components/common/Icon';
import Toggle from '@components/common/Toggle';
import {
    TableRowActionsTooltipProvider,
    TableRowIconActions,
} from '@components/common/TableRowIconActions';
import Button from '@components/Button/Button';
import Select from '@components/Form/Select/Select';
import Input from '@/components/Form/Input/Input';
import CreateCourseModal, { type CourseForm } from './CreateCourseModal';
import EditCourseModal, { type EditCourseForm } from './EditCourseModal';
import PageHeader from '@components/common/PageHeader';
import ConfirmModal from '@components/common/ConfirmModal/ConfirmModal';
import coursesService, { type CourseDatum } from '@/services/courses/coursesService';
import { useAuth } from '@/context/AuthContext';
import { getApiErrorMessage } from '@/utils/apiValidationErrors';

const CoursesPage = () => {
    const { t } = useTranslation('courses');
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { isSuperAdmin } = useAuth();

    const statusOptions = useMemo(() => [
        { value: '', label: t('filters.all_status') },
        { value: 'ACTIVE', label: t('filters.active') },
        { value: 'INACTIVE', label: t('filters.inactive') },
    ], [t]);

    // Permissions — Admin can perform all actions
    const canCreate = isSuperAdmin;
    const canEdit = isSuperAdmin;
    const canDelete = isSuperAdmin;
    const canActivate = isSuperAdmin;

    // Filters and pagination state
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [pageIndex, setPageIndex] = useState(0);
    const [debouncedSearch, setDebouncedSearch] = useState(search);
    const [showCreate, setShowCreate] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [editingCourse, setEditingCourse] = useState<CourseDatum | null>(null);
    const [statusUpdatingId, setStatusUpdatingId] = useState<string | null>(null);
    const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
    const PAGE_SIZE = 20;

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(timer);
    }, [search]);

    const listQueryKey = ['courses', pageIndex, PAGE_SIZE, statusFilter, debouncedSearch];

    const { data: coursesResponse, isLoading, isError } = useQuery({
        queryKey: listQueryKey,
        queryFn: () =>
            coursesService.getCourses(
                pageIndex + 1,
                PAGE_SIZE,
                statusFilter || undefined,
                debouncedSearch.trim() || undefined,
            ),
        placeholderData: previousData => previousData,
        staleTime: 5 * 60 * 1000,
    });

    const courses = useMemo<CourseDatum[]>(
        () => coursesResponse?.data?.data ?? [],
        [coursesResponse],
    );

    const editCourseInitialData = useMemo<EditCourseForm | null>(
        () =>
            editingCourse
                ? {
                      course_name: editingCourse.course_name,
                      description: editingCourse.description,
                      is_active: editingCourse.status === 'ACTIVE',
                  }
                : null,
        [editingCourse],
    );

    const createCourseMutation = useMutation({
        mutationFn: async (payload: CourseForm) =>
            await coursesService.createCourse({
                course_name: payload.course_name,
                description: payload.description,
                status: payload.is_active ? 'ACTIVE' : 'INACTIVE',
            }),
        onSuccess: (response) => {
            toast.success(response.message ?? t('toast.create_success'));
            setShowCreate(false);
            queryClient.invalidateQueries({ queryKey: ['courses'] });
        },
        onError: (error: unknown) => {
            toast.error(getApiErrorMessage(error, t('toast.create_error')));
        },
    });

    const updateCourseMutation = useMutation({
        mutationFn: ({ id, payload }: { id: string; payload: EditCourseForm }) =>
            coursesService.updateCourse(id, {
                course_name: payload.course_name,
                description: payload.description,
                status: payload.is_active ? 'ACTIVE' : 'INACTIVE',
            }),
        onSuccess: (response) => {
            toast.success(response.message ?? t('toast.update_success'));
            setShowEdit(false);
            setEditingCourse(null);
            queryClient.invalidateQueries({ queryKey: ['courses'] });
        },
        onError: (error: unknown) => {
            toast.error(getApiErrorMessage(error, t('toast.update_error')));
        },
    });

    const toggleCourseStatusMutation = useMutation({
        mutationFn: ({ course, nextStatus }: { course: CourseDatum; nextStatus: boolean }) =>
            coursesService.updateCourse(course.id, {
                status: nextStatus ? 'ACTIVE' : 'INACTIVE',
            }),
        onMutate: async ({ course, nextStatus }) => {
            await queryClient.cancelQueries({ queryKey: listQueryKey });
            const previousResponse = queryClient.getQueryData(listQueryKey);

            queryClient.setQueryData(listQueryKey, (oldData: typeof coursesResponse) => {
                if (!oldData?.data?.data) return oldData;
                return {
                    ...oldData,
                    data: {
                        ...oldData.data,
                        data: oldData.data.data.map(item =>
                            item.id === course.id ? { ...item, status: nextStatus ? 'ACTIVE' : 'INACTIVE' } : item,
                        ),
                    },
                };
            });

            return { previousResponse };
        },
        onSuccess: (response) => {
            toast.success(response.message ?? t('toast.status_success'));
            queryClient.invalidateQueries({ queryKey: ['courses'] });
        },
        onError: (error: any, _variables, context) => {
            if (context?.previousResponse) {
                queryClient.setQueryData(listQueryKey, context.previousResponse);
            }
            toast.error(error?.response?.data?.message ?? t('toast.status_error'));
        },
        onSettled: () => {
            setStatusUpdatingId(null);
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => coursesService.deleteCourse(id),
        onSuccess: () => {
            toast.success(t('toast.delete_success'));
            void queryClient.invalidateQueries({ queryKey: ['courses'] });
        },
        onError: (error: { response?: { data?: { message?: string } } }) => {
            toast.error(error?.response?.data?.message ?? t('toast.delete_error'));
        },
    });

    const columns = useMemo<ColumnDef<CourseDatum, unknown>[]>(() => {
        const cols: ColumnDef<CourseDatum, unknown>[] = [
            {
                accessorKey: 'course_name',
                header: t('table.program_name'),
                size: 300,
                cell: ({ row }) => (
                    <button
                        type="button"
                        className="text-left font-bold text-[13px] text-[var(--primary-color)] hover:underline cursor-pointer bg-transparent border-0 p-0"
                        onClick={() => navigate(`/admin/courses/${row.original.id}`)}
                    >
                        {row.original.course_name}
                    </button>
                ),
            },
            {
                accessorKey: 'description',
                header: t('table.description'),
                size: 400,
                cell: ({ row }) => (
                    <span className="text-[13px] text-[var(--subtitle-color)] line-clamp-1 truncate block max-w-[380px]" title={row.original.description}>
                        {row.original.description}
                    </span>
                ),
            },
        ];

        if (canActivate) {
            cols.push({
                accessorKey: 'status',
                header: t('table.status'),
                size: 160,
                enableSorting: false,
                cell: ({ row }) => (
                    <Toggle
                        active={row.original.status === 'ACTIVE'}
                        disabled={statusUpdatingId === row.original.id}
                        onChange={(nextStatus) => {
                            setStatusUpdatingId(row.original.id);
                            toggleCourseStatusMutation.mutate({
                                course: row.original,
                                nextStatus,
                            });
                        }}
                    />
                ),
            });
        } else {
            cols.push({
                accessorKey: 'status',
                header: t('table.status'),
                size: 160,
                enableSorting: false,
                cell: ({ row }) => (
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${row.original.status === 'ACTIVE' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-rose-50 text-rose-600 border border-rose-100'}`}>
                        {row.original.status === 'ACTIVE' ? t('filters.active') : t('filters.inactive')}
                    </span>
                ),
            });
        }

        if (canEdit || canDelete) {
            cols.push({
                id: 'actions',
                header: t('table.action'),
                size: 120,
                minSize: 120,
                enableSorting: false,
                cell: ({ row }) => {
                    const course = row.original;
                    const actions = [
                        ...(canEdit
                            ? [
                                {
                                    icon: 'edit' as const,
                                    label: t('actions.edit'),
                                    onClick: () => {
                                        setEditingCourse(course);
                                        setShowEdit(true);
                                    },
                                },
                              ]
                            : []),
                        ...(canDelete
                            ? [
                                {
                                    icon: 'delete' as const,
                                    label: t('actions.delete'),
                                    onClick: () => setPendingDeleteId(course.id),
                                    variant: 'danger' as const,
                                },
                              ]
                            : []),
                    ];
                    return <TableRowIconActions actions={actions} />;
                },
            });
        }

        return cols;
    }, [navigate, canEdit, canDelete, canActivate, statusUpdatingId, toggleCourseStatusMutation, t]);

    const searchIcon = (
        <button className="flex items-center cursor-pointer">
            <Icon name='search' className='w-3 2xl:w-[14px] h-auto' />
        </button>
    );

    return (
        <TableRowActionsTooltipProvider>
            <>
                <PageHeader
                    title={t('page_title')}
                    breadcrumbItems={[
                        { label: t('breadcrumb'), to: '/admin/dashboard' },
                        { label: t('breadcrumb_program') },
                    ]}
                    actions={
                        <>
                            {canCreate && (
                                <Button onClick={() => setShowCreate(true)}>
                                    <Icon name="plus" className='mr-1 md:mr-2 mt-[-1px] w-3 2xl:w-4 h-auto' />
                                    {t('buttons.create')}
                                </Button>
                            )}
                        </>
                    }
                />
                <div className="flex flex-col gap-4 2xl:gap-5 !pt-0 p-3 md:p-4 2xl:p-5">
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 lg:gap-4 2xl:gap-5 p-3 2xl:p-5 pb-[2px] 2xl:pb-[5px] bg-white rounded-2xl border border-[var(--neutral-color)]">
                        <div className='lg:col-start-4'>
                            <Input
                                type="text"
                                placeholder={t('filters.search_placeholder')}
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                leftElement={searchIcon}
                            />
                        </div>
                        <div>
                            <Select
                                placeholder={t('filters.all_status')}
                                value={statusFilter}
                                onChange={val => setStatusFilter(val)}
                                options={statusOptions}
                            />
                        </div>
                    </div>
                    {isError ? (
                        <div className="bg-white rounded-2xl border border-[var(--neutral-color)] p-4 text-sm text-[var(--semantic-error-color)]">
                            {t('errors.load_failed')}
                        </div>
                    ) : (
                        <DataGridTable
                            data={courses}
                            columns={columns}
                            totalCount={coursesResponse?.data?.total ?? 0}
                            pageIndex={pageIndex}
                            pageSize={PAGE_SIZE}
                            onPageChange={p => setPageIndex(p)}
                            loading={isLoading}
                        />
                    )}
                </div>
                <CreateCourseModal
                    isOpen={showCreate}
                    onClose={() => setShowCreate(false)}
                    onSubmit={async (formData) => {
                        await createCourseMutation.mutateAsync(formData);
                    }}
                    isSubmitting={createCourseMutation.isPending}
                />
                <EditCourseModal
                    isOpen={showEdit}
                    onClose={() => {
                        setShowEdit(false);
                        setEditingCourse(null);
                    }}
                    initialData={editCourseInitialData}
                    onSubmit={async (formData) => {
                        if (!editingCourse) return;
                        await updateCourseMutation.mutateAsync({
                            id: editingCourse.id,
                            payload: formData,
                        });
                    }}
                    isSubmitting={updateCourseMutation.isPending}
                />
                <ConfirmModal
                    isOpen={pendingDeleteId !== null}
                    onClose={() => setPendingDeleteId(null)}
                    onConfirm={() => {
                        if (pendingDeleteId !== null) {
                            deleteMutation.mutate(pendingDeleteId);
                            setPendingDeleteId(null);
                        }
                    }}
                    loading={deleteMutation.isPending}
                    title={t('toast.delete_title')}
                    message={t('toast.delete_confirm')}
                    confirmLabel={t('actions.delete')}
                    variant="danger"
                />
            </>
        </TableRowActionsTooltipProvider>
    );
};

export default CoursesPage;
